import { html } from "htm/preact";
import { useRef, useState } from "preact/hooks";
import { navigate } from "../router.js";
import { Icon } from "../components/Icon.js";
import { TERMS } from "../copy.js";
import { parsePastedLink, type PastedLink } from "../organiserLink.js";
import { restoreOrganiserAccess } from "../organiserAccess.js";

/**
 * Typing your way back into a game you organise.
 *
 * The recovery link works everywhere except the one place it is most needed. An app
 * added to a phone's home screen keeps its own storage, separate from the browser's,
 * and a tapped link opens the browser — so following the link restores access to the
 * wrong copy of the app, over and over, with no way to cross from one to the other.
 * Pasting sidesteps the whole problem: nothing has to route anywhere.
 *
 * Players never need this. They type the four-letter code and take their name back off
 * the roster. This screen is the organiser's version of that, and the reason it can't
 * be as simple is the same reason it's worth having — the organiser's token is
 * unguessable by design.
 */
export function RestoreAccess() {
  const [text, setText] = useState("");
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hint, setHint] = useState<string | null>(null);
  const box = useRef<HTMLTextAreaElement | null>(null);

  const parsed = parsePastedLink(text);

  async function readClipboard() {
    setError(null);
    try {
      // `read()` before `readText()`, because a link copied through a share sheet can
      // land on the clipboard as a URL-typed item whose plain-text side is only the
      // message it was sent with. Where that happens, the URL is in another flavour or
      // it is nowhere. Best effort: iOS may refuse `read()` outright, in which case
      // plain text is still better than nothing.
      const rich = await readRichClipboard();
      setText(rich ?? (await navigator.clipboard.readText()));
      setHint(null);
    } catch {
      // Denied, dismissed, or a context the API won't run in. The box below is the
      // answer to all three, so point at it rather than explaining which one it was.
      setHint("Paste it into the box instead.");
      box.current?.focus();
    }
  }

  /** Takes the link out of a paste when the plain text alone doesn't carry one. Falls
   * through to the browser's own paste whenever it has nothing to add, because that is
   * the path that works everywhere else and must not be broken in the name of this. */
  function onPaste(e: ClipboardEvent) {
    const data = e.clipboardData;
    if (!data) return;
    const plain = data.getData("text/plain") ?? "";
    if (parsePastedLink(plain)?.kind === "organiser") return;

    const url = urlFromFlavours(data);
    if (!url) return;

    e.preventDefault();
    setText(plain ? `${plain}\n${url}` : url);
    setError(null);
  }

  async function submit(e?: Event) {
    e?.preventDefault();
    setError(null);
    const target = resolve(parsed, code);
    if (!target) return;

    setBusy(true);
    try {
      await restoreOrganiserAccess(target.code, target.token);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  }

  const target = resolve(parsed, code);

  return html`
    <h1 class="mb-0">${`Restore ${TERMS.organizer} access`}</h1>
    <p class="field-help">
      ${`Paste the ${TERMS.organizer} link for your ${TERMS.event} — the one you saved when you
        set it up. Following the link opens your browser, which is a different app from
        this one, so pasting it here is how access gets in.`}
    </p>
    <p class="field-help">
      ${`Paste the whole message if that's easier — the note it was sent with, line breaks
        and all. Only the link gets read.`}
    </p>

    <form class="stack" onSubmit=${submit}>
      <label class="field">
        <span class="field-label">${`${TERMS.Organizer} link`}</span>
        <textarea
          ref=${box}
          rows="4"
          class="paste-box"
          placeholder=${`Paste the link, or the whole message it came in`}
          value=${text}
          autocapitalize="off"
          autocorrect="off"
          autocomplete="off"
          spellcheck="false"
          onPaste=${onPaste}
          onInput=${(e: any) => {
            setText(e.currentTarget.value);
            setError(null);
          }}
        ></textarea>
      </label>

      ${typeof navigator !== "undefined" &&
      typeof navigator.clipboard?.readText === "function" &&
      html`
        <button type="button" class="secondary" onClick=${readClipboard}>
          <${Icon} name="copy" size=${18} /> Paste from clipboard
        </button>
      `}
      ${hint && html`<p class="field-help mt-0">${hint}</p>`}

      <!-- A token with no game around it still gets you in, but we have to ask which
           game — the code is not recoverable from the token. -->
      ${parsed?.kind === "tokenOnly" &&
      html`
        <label class="field">
          <span class="field-label">${`${TERMS.Event} code`}</span>
          <input
            class="code-input"
            value=${code}
            maxlength="4"
            placeholder="ABCD"
            autocapitalize="characters"
            autocomplete="off"
            spellcheck="false"
            onInput=${(e: any) => setCode(e.currentTarget.value.toUpperCase())}
          />
          <span class="field-help">
            That's a ${TERMS.organizer} token without its ${TERMS.event} code. Which
            ${TERMS.event} is it for?
          </span>
        </label>
      `}

      <!-- Says which game before committing, so a link for the wrong one is caught by
           the person who pasted it rather than by the screen they land on. -->
      ${target &&
      html`<p class="notice notice-ok mt-0">
        <${Icon} name="check" size=${16} /> ${`This will restore ${TERMS.event} ${target.code}.`}
      </p>`}

      <button type="submit" disabled=${busy || !target}>
        ${busy ? "Checking…" : `Restore access`}
      </button>
    </form>

    ${parsed?.kind === "join" &&
    html`
      <div class="card card-accent">
        <p>
          ${`That's the invite link for ${TERMS.event} ${parsed.code} — the one players use.
            It won't make you the ${TERMS.organizer}, but it will get you into the ${TERMS.event}.`}
        </p>
        <button class="secondary" onClick=${() => navigate(`/join/${parsed.code}`)}>
          ${`Join ${parsed.code} instead`}
        </button>
      </div>
    `}
    ${parsed?.kind === "incomplete" &&
    html`
      <p class="notice notice-error">
        <${Icon} name="alert" size=${16} />${" "}
        ${parsed.code
          ? `That's the link for ${TERMS.event} ${parsed.code}, but the long part after
             "organizer" is cut short — links get truncated when they're forwarded. Copy it
             again from wherever you saved it.`
          : `That looks like an ${TERMS.organizer} link that got cut short. Copy it again from
             wherever you saved it.`}
      </p>
    `}
    <!-- Text with no link in it. Worth its own branch rather than leaving the button
         disabled and saying nothing: the message an organiser link is shared with can
         reach the clipboard *without* the link — the share sheet keeps the two apart —
         and someone pasting that gets a screen where nothing happens and nothing
         explains why. -->
    ${text.trim() !== "" &&
    parsed === null &&
    html`
      <p class="notice notice-error">
        <${Icon} name="alert" size=${16} />${" "}
        ${strandedMessage(text)
          ? `That's the message the link was sent with, but the link itself didn't come
             with it — copy it again and check you've got the https:// part too.`
          : `There's no ${TERMS.organizer} link in that. It should have an https:// address
             with "organizer" in it — paste the whole message if that's easier.`}
      </p>
    `}
    ${error && html`<p class="notice notice-error"><${Icon} name="alert" size=${16} /> ${error}</p>`}

    <p class="back-link mt-4"><a href="#/">← Home</a></p>
  `;
}

/**
 * Whether this looks like the note an organiser link is shared with, arriving on its
 * own. Recognising it is worth a sentence of its own — it is not a typo or the wrong
 * link, it is the exact failure that brought people to this screen, and the answer
 * ("copy it again, with the https:// part") is different from the general one.
 */
function strandedMessage(text: string): boolean {
  return /organi[sz]er/i.test(text) && !/https?:\/\//i.test(text);
}

/** The other things a clipboard can be carrying besides the text a textarea receives.
 * `text/uri-list` is the flavour a shared link lives in; the HTML flavour keeps it in an
 * href. Either will do — the parser only needs the link to be somewhere in the string. */
function urlFromFlavours(data: DataTransfer): string | null {
  const list = data.getData("text/uri-list");
  const fromList = list?.split(/\r?\n/).find((line) => line && !line.startsWith("#"));
  if (fromList) return fromList.trim();

  const href = /href=["']([^"']+)["']/i.exec(data.getData("text/html") ?? "");
  return href?.[1] ?? null;
}

/** The async-clipboard equivalent, for the paste button. Returns null rather than
 * throwing when there's nothing better than plain text, so the caller can fall back. */
async function readRichClipboard(): Promise<string | null> {
  if (typeof navigator.clipboard?.read !== "function") return null;
  try {
    for (const item of await navigator.clipboard.read()) {
      if (!item.types.includes("text/uri-list")) continue;
      const url = (await (await item.getType("text/uri-list")).text()).trim();
      if (!url) continue;
      const plain = item.types.includes("text/plain")
        ? await (await item.getType("text/plain")).text()
        : "";
      return plain && !plain.includes(url) ? `${plain}\n${url}` : url;
    }
  } catch {
    // Not supported, or refused. The caller still has readText().
  }
  return null;
}

/** What a Restore press would actually use, or null if there isn't enough yet. */
function resolve(parsed: PastedLink, code: string): { code: string; token: string } | null {
  if (parsed?.kind === "organiser") return { code: parsed.code, token: parsed.token };
  if (parsed?.kind === "tokenOnly" && code.length === 4) return { code, token: parsed.token };
  return null;
}
